'use client';

// `useActiveNetwork()` — 3-state companion to `useNetworkMode()`.
//
// Returns the user's effective network as one of `'local' | 'devnet' |
// 'mainnet'`. Use this for INFRASTRUCTURE bindings — anything that
// configures a backend origin, RPC endpoint, query cache, or session
// cookie that is keyed by network:
//
//   • SdkProvider           (apiUrl from getNetworkConfig — different
//                            for local vs devnet)
//   • NetworkSyncProvider   (query-cache invalidation on origin change)
//   • NetworkAuthBridge     (re-establish session when backend origin
//                            differs)
//
// For UX/URL decisions (NetworkLink prefix, "demo mode" banner, etc.),
// use `useNetworkMode()` instead — that hook collapses 'local' into
// 'devnet' because at the user-visible level they're indistinguishable.
//
// ─── The rule (two lines) ──────────────────────────────────────────
//
//   1. If stored preference is `'mainnet'` AND the `mainnet_enabled`
//      gate is OFF → return `'devnet'`. The gate ONLY clamps mainnet;
//      `'local'` is engineer-only and bypasses the gate entirely
//      (engineers running with `_LOCAL_*` env vars know what they're
//      doing).
//
//   2. Otherwise return the stored preference verbatim.
import { useDevToolkitStore } from '@/stores/dev-toolkit';
import { isMainnetEnabled } from './mainnet-enabled';
import { useNetworkPresentation } from './presentation-context';
import { useNetworkStore } from './store';
import type { Network } from './types';
export function useActiveNetwork() {
  const presentation = useNetworkPresentation();
  useDevToolkitStore(_temp);
  const stored = useNetworkStore(_temp2);
  if (presentation !== null) {
    return presentation.network;
  }
  if (stored === "mainnet" && !isMainnetEnabled()) {
    return "devnet";
  }
  return stored;
}
function _temp2(s_0) {
  return s_0.current;
}
function _temp(s) {
  return s.overrides;
}